import { type Node } from '@xyflow/react'
import { type IFlowAction } from 'widgets/Flow'

const VERTICAL_GAP_WITH_LABEL = 196
const VERTICAL_GAP_WITH_CHILDREN = 213
const VERTICAL_GAP = 94
const TOP_PADDING = 24
const BRANCH_GAP = 320

export const getNodesByChangesMap = ({
  nodeChangesMap,
  getActionById,
  containerWidth,
}: {
  nodeChangesMap: Map<string, Node>
  getActionById: (id: string) => IFlowAction | undefined
  containerWidth: number
}) => {
  const triggerAction = getActionById('trigger')

  let treeDegree = 0

  const findMaxDegree = (id: string, degree: number) => {
    const action = getActionById(id)
    if (action) {
      if (action.actionStore.nextIds.length === 1) {
        findMaxDegree(action.actionStore.nextIds[0], degree)
      }
      if (action.actionStore.nextIds.length > 1) {
        action.actionStore.nextIds.forEach((id: string) => {
          findMaxDegree(id, degree + 1)
        })
      } else {
        treeDegree = Math.max(treeDegree, degree)
      }
    }
  }
  if (triggerAction?.actionStore.nextId) {
    findMaxDegree(triggerAction?.actionStore.nextId, 0)
  }

  const newNodes: Node[] = []
  const maxWidth = BRANCH_GAP * 2 ** treeDegree

  const drawNode = (action: IFlowAction, branches: number[], y: number) => {
    const node = nodeChangesMap.get(action.actionStore.id)

    if (!node) {
      return
    }

    const ownWidth = node.measured?.width ?? action?.actionStore.defaultWidth ?? 0
    const nodeHeight = node.measured?.height || action?.actionStore.defaultHeight || 0

    const centerX = containerWidth / 2 - ownWidth / 2

    const getX = () => {
      if (branches.length === 0) {
        return centerX
      }
      const branchIndex = parseInt(branches.join(''), 2)

      const elementCount = 2 ** branches.length
      const gap = maxWidth / elementCount

      const branchX = branchIndex * gap - maxWidth / 2 + gap / 2

      return centerX + branchX
    }
    newNodes.push({
      ...node,
      position: {
        x: getX(),
        y,
      },
    })

    const branchGap =
      action.actionStore.edgeType === 'customEdgeWithLabel'
        ? action.actionStore.nextIds.length > 1
          ? VERTICAL_GAP_WITH_CHILDREN
          : VERTICAL_GAP_WITH_LABEL
        : VERTICAL_GAP

    const getGapForChild = (childId: string) => {
      const child = getActionById(childId)
      return child?.actionStore.type === 'addButton' ? 38 : branchGap
    }

    if (action.actionStore.nextIds.length > 1) {
      action.actionStore.nextIds.forEach((id: string, index) => {
        const childAction = getActionById(id)
        const newBranches = [...branches, index]

        if (!childAction) return

        drawNode(childAction, newBranches, y + nodeHeight + getGapForChild(id))
      })
    } else {
      action.actionStore.nextIds.forEach((id: string) => {
        const childAction = getActionById(id)
        if (!childAction) return
        drawNode(childAction, branches, y + nodeHeight + getGapForChild(id))
      })
    }
  }
  if (triggerAction) {
    drawNode(triggerAction, [], TOP_PADDING)
  }

  return newNodes
}
